import { Search, Calendar, Clock, User, UserPlus, AlertTriangle } from "lucide-react";
import { ReassignModal } from "../modals/ReassignModal";
import { APPOINTMENT_STATUS } from "../constants";
import { useState } from "react";

export const AppointmentList = ({
  appointments = [],
  onCheckIn,
  searchQuery = "",
  onSearchChange,
  onReassign
}) => {
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const filteredAppointments = appointments.filter((appointment) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      appointment.client?.fullName
        ?.toLowerCase()
        .includes(searchLower) ||
      appointment.counselor?.fullName
        ?.toLowerCase()
        .includes(searchLower)
    );
  });

  const formatTime = (time) => {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const handleReassign = async (appointmentId, reassignData) => {
    await onReassign(appointmentId, reassignData);
    setShowReassignModal(false);
    setSelectedAppointment(null);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-medium">Today's Appointments</h2>
        <div className="mt-4 relative">
          <input
            type="text"
            placeholder="Search appointments..."
            className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-uzima-maroon-400"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
      </div>

      <div className="divide-y">
        {filteredAppointments.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No appointments found
          </div>
        ) : (
          filteredAppointments.map((appointment) => (
            <div key={appointment._id} className="p-4 hover:bg-gray-50">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {appointment.client?.fullName}
                    </span>
                    {appointment.isUrgent && (
                      <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Urgent
                      </span>
                    )}
                  </div>

                  <div className="mt-1 text-sm text-gray-500 space-y-1">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Counselor: {appointment.counselor?.fullName}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Time: {formatTime(appointment.timeSlot)}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Date: {new Date(appointment.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                {appointment.status === APPOINTMENT_STATUS.SCHEDULED && !appointment.checkedIn && (
                  <>
                    <button
                      onClick={() => onCheckIn(appointment._id)}
                      className="px-3 py-1 text-sm bg-green-50 text-green-600 rounded-md hover:bg-green-100"
                    >
                      Check In
                    </button>
                    <button
                      onClick={() => {
                        setSelectedAppointment(appointment);
                        setShowReassignModal(true);
                      }}
                      className="px-3 py-1 text-sm bg-uzima-maroon-50 text-uzima-maroon-500 rounded-md hover:bg-blue-100 flex items-center gap-1"
                    >
                      <UserPlus className="h-4 w-4" />
                      Reassign
                    </button>
                  </>
                )}

                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      appointment.status === APPOINTMENT_STATUS.SCHEDULED
                        ? "bg-blue-100 text-blue-800"
                        : appointment.status === APPOINTMENT_STATUS.COMPLETED
                        ? "bg-green-100 text-green-800"
                        : appointment.status === APPOINTMENT_STATUS.CANCELLED
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {appointment.status.charAt(0).toUpperCase() +
                      appointment.status.slice(1)}
                  </span>
                </div>
              </div>

              {appointment.urgencyReason && (
                <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                  <strong>Urgency Reason:</strong> {appointment.urgencyReason}
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => {
          setShowReassignModal(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onReassign={handleReassign}
      />
    </div>
  );
};
